import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Form,
  Switch,
  Select,
  Button,
  message,
  Space,
  Alert,
  Divider,
  Row,
  Col,
  Input,
  InputNumber,
  Table,
  Modal,
  Grid,
  Tag
} from 'antd';
import {
  ThunderboltOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';

const { Option } = Select;
const { useBreakpoint } = Grid;

export default function Optimization() {
  const { domainData, reloadDomain } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  // 用 useWatch 订阅开关值，保证切换「启用 Early Hints」时下方资源区能即时显隐
  const earlyHintsEnabled = Form.useWatch('early_hints_enabled', form);
  // 订阅 WebP 开关，控制细控项显隐
  const webpEnabled = Form.useWatch('webp_enabled', form);

  // Early Hints 配置
  const [earlyHintsLinks, setEarlyHintsLinks] = useState([]);
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [linkForm] = Form.useForm();

  useEffect(() => {
    if (domainData) {
      // 从domain数据或rules中提取优化设置
      const settings = domainData.optimization_settings || {};

      form.setFieldsValue({
        minify_html: settings.minify_html || false,
        minify_css: settings.minify_css || false,
        minify_js: settings.minify_js || false,
        gzip_enabled: settings.gzip_enabled !== undefined ? settings.gzip_enabled : true,
        brotli_enabled: settings.brotli_enabled || false,
        zstd_enabled: settings.zstd_enabled || false,
        // 压缩细控（0 = 使用边缘库默认级别；min/max = 字节，0 = 库默认门限）
        gzip_level: settings.gzip_level ?? 0,
        brotli_level: settings.brotli_level ?? 0,
        zstd_level: settings.zstd_level ?? 0,
        compress_min_length: settings.compress_min_length ?? 0,
        compress_max_length: settings.compress_max_length ?? 0,
        compress_types: Array.isArray(settings.compress_types) ? settings.compress_types : [],
        // 字符集（响应 Content-Type charset 覆盖，对齐边缘 charset 模块）
        charset_enabled: settings.charset_enabled || false,
        charset: settings.charset || 'utf-8',
        charset_force: settings.charset_force || false,
        charset_uppercase: settings.charset_uppercase || false,
        charset_content_types: Array.isArray(settings.charset_content_types) ? settings.charset_content_types : [],
        http3_enabled: settings.http3_enabled ?? true, // on unless explicitly turned off
        // WebP 转码（边缘 buildWebpConfig 读取 webp_*）
        webp_enabled: settings.webp_enabled || false,
        webp_quality: settings.webp_quality ?? 80,
        webp_lossless: settings.webp_lossless || false,
        webp_min_length: settings.webp_min_length ?? 0,
        webp_max_length: settings.webp_max_length ?? 0
      });

      // 加载 Early Hints 配置
      const earlyHintsEnabled = domainData.early_hints_enabled || false;
      const links = domainData.early_hints_links || [];

      form.setFieldsValue({
        early_hints_enabled: earlyHintsEnabled
      });

      // 解析 early_hints_links
      if (typeof links === 'string') {
        try {
          setEarlyHintsLinks(JSON.parse(links));
        } catch (e) {
          setEarlyHintsLinks([]);
        }
      } else if (Array.isArray(links)) {
        setEarlyHintsLinks(links);
      }
    }
  }, [domainData, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // 保存到domain的优化设置中
      const response = await cdnService.updateDomain({
        id: domainData.id,
        optimization_settings: {
          minify_html: values.minify_html,
          minify_css: values.minify_css,
          minify_js: values.minify_js,
          gzip_enabled: values.gzip_enabled,
          gzip_level: values.gzip_level ?? 0,
          brotli_enabled: values.brotli_enabled,
          zstd_enabled: values.zstd_enabled,
          brotli_level: values.brotli_level ?? 0,
          zstd_level: values.zstd_level ?? 0,
          compress_min_length: values.compress_min_length ?? 0,
          compress_max_length: values.compress_max_length ?? 0,
          compress_types: Array.isArray(values.compress_types) ? values.compress_types : [],
          charset_enabled: values.charset_enabled,
          charset: values.charset,
          charset_force: values.charset_force,
          charset_uppercase: values.charset_uppercase,
          charset_content_types: Array.isArray(values.charset_content_types) ? values.charset_content_types : [],
          http2_enabled: true,
          http3_enabled: values.http3_enabled,
          webp_enabled: values.webp_enabled,
          webp_quality: values.webp_quality ?? 80,
          webp_lossless: values.webp_lossless,
          webp_min_length: values.webp_min_length ?? 0,
          webp_max_length: values.webp_max_length ?? 0
        },
        early_hints_enabled: values.early_hints_enabled,
        early_hints_links: earlyHintsLinks
      });

      if (response.success) {
        message.success('保存成功');
        reloadDomain();
      } else {
        message.error(response.error || '保存失败');
      }
    } catch (error) {
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  // Early Hints 链接管理
  const handleAddLink = () => {
    setEditingLink(null);
    linkForm.resetFields();
    setLinkModalVisible(true);
  };

  const handleEditLink = (link, index) => {
    setEditingLink(index);
    linkForm.setFieldsValue(link);
    setLinkModalVisible(true);
  };

  const handleDeleteLink = (index) => {
    const newLinks = earlyHintsLinks.filter((_, i) => i !== index);
    setEarlyHintsLinks(newLinks);
    message.success('已删除');
  };

  const handleSaveLink = async () => {
    try {
      const values = await linkForm.validateFields();

      if (editingLink !== null) {
        // 编辑现有链接
        const newLinks = [...earlyHintsLinks];
        newLinks[editingLink] = values;
        setEarlyHintsLinks(newLinks);
      } else {
        // 添加新链接
        setEarlyHintsLinks([...earlyHintsLinks, values]);
      }

      setLinkModalVisible(false);
      linkForm.resetFields();
      message.success(editingLink !== null ? '已更新' : '已添加');
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // Mobile card renderer for Early Hints
  const renderMobileEarlyHintsCard = (link, index) => {
    const typeMap = {
      style: 'CSS',
      script: 'JavaScript',
      font: '字体',
      image: '图片',
      fetch: '数据',
      document: 'HTML'
    };

    return (
      <Card key={index} size="small" style={{ marginBottom: 8 }}>
        <div style={{ marginBottom: 8 }}>
          <div style={{ wordBreak: 'break-all', fontWeight: 500, marginBottom: 4 }}>
            {link.url}
          </div>
          <Space wrap size="small">
            <Tag color="blue">{typeMap[link.as] || link.as}</Tag>
            {link.type && <Tag>{link.type}</Tag>}
            {link.crossorigin && <Tag color="orange">{link.crossorigin}</Tag>}
          </Space>
        </div>
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditLink(link, index)}>
            编辑
          </Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteLink(index)}>
            删除
          </Button>
        </Space>
      </Card>
    );
  };

  return (
    <div style={{ padding: isMobile ? '12px 0' : 0 }}>
      <Card
        title={
          <Space>
            <ThunderboltOutlined />
            性能优化
          </Space>
        }
        extra={
          !isMobile && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              loading={loading}
            >
              保存设置
            </Button>
          )
        }
        size={isMobile ? 'small' : 'default'}
      >
        {isMobile && (
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              loading={loading}
              size="small"
              block
            >
              保存设置
            </Button>
          </div>
        )}

        <Alert
          message="性能优化"
          description={isMobile ? "启用这些功能可提高网站加载速度" : "启用这些功能可以显著提高网站的加载速度和用户体验。部分功能可能需要测试确保与您的网站兼容。"}
          type="info"
          showIcon
          style={{ marginBottom: isMobile ? 16 : 24 }}
        />

        <Form
          form={form}
          layout="vertical"
        >
          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>内容压缩</Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? "HTML" : "压缩 HTML"}
                name="minify_html"
                valuePropName="checked"
                tooltip={!isMobile ? "移除HTML中的空白字符和注释，减少文件大小" : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? "CSS" : "压缩 CSS"}
                name="minify_css"
                valuePropName="checked"
                tooltip={!isMobile ? "移除CSS中的空白字符和注释，减少文件大小" : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? "JS" : "压缩 JavaScript"}
                name="minify_js"
                valuePropName="checked"
                tooltip={!isMobile ? "移除JS中的空白字符和注释，减少文件大小" : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>传输压缩</Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label="Gzip"
                name="gzip_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? "使用Gzip算法压缩响应内容，通常可减少70%的传输大小" : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item label="Brotli" name="brotli_enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item label="Zstandard" name="zstd_enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>压缩细控</Divider>

          <Alert
            message="留 0 使用推荐默认（gzip 6 / brotli 4 / zstd 3，最小 1024 字节）"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? "Gzip级别" : "Gzip 级别 (0-9)"}
                name="gzip_level"
                tooltip={!isMobile ? "1=最快，9=最高压缩比；0=库默认(6)" : undefined}
              >
                <InputNumber min={0} max={9} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item label={isMobile ? "Brotli级别" : "Brotli 级别 (0-11)"} name="brotli_level">
                <InputNumber min={0} max={11} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item label={isMobile ? "Zstd级别" : "Zstd 级别 (0-22)"} name="zstd_level">
                <InputNumber min={0} max={22} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Form.Item
                label={isMobile ? "最小字节" : "最小压缩体积 (字节)"}
                name="compress_min_length"
                tooltip={!isMobile ? "响应体小于此值不压缩；0=默认 1024" : undefined}
              >
                <InputNumber min={0} step={256} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Form.Item
                label={isMobile ? "最大字节" : "最大压缩体积 (字节)"}
                name="compress_max_length"
                tooltip={!isMobile ? "响应体大于此值不压缩（避免大文件耗 CPU）；0=无上限" : undefined}
              >
                <InputNumber min={0} step={1024} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                label={isMobile ? "类型白名单" : "内容类型白名单"}
                name="compress_types"
                tooltip={!isMobile ? "仅压缩这些 Content-Type（支持 text/* 前缀）；留空=默认文本类白名单" : undefined}
              >
                <Select
                  mode="tags"
                  allowClear
                  placeholder="text/html, application/json, text/*"
                  tokenSeparators={[',', ' ']}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>字符集</Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={12} sm={8} md={6}>
              <Form.Item
                label={isMobile ? "启用" : "启用字符集覆盖"}
                name="charset_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? "为文本类响应补齐/覆盖 Content-Type 的 charset 参数" : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item
                label="字符集"
                name="charset"
                tooltip={!isMobile ? "目标编码，如 utf-8、gbk" : undefined}
              >
                <Select showSearch style={{ width: '100%' }}>
                  <Option value="utf-8">utf-8</Option>
                  <Option value="gbk">gbk</Option>
                  <Option value="gb2312">gb2312</Option>
                  <Option value="gb18030">gb18030</Option>
                  <Option value="big5">big5</Option>
                  <Option value="iso-8859-1">iso-8859-1</Option>
                  <Option value="shift_jis">shift_jis</Option>
                  <Option value="euc-jp">euc-jp</Option>
                  <Option value="euc-kr">euc-kr</Option>
                  <Option value="windows-1251">windows-1251</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item
                label={isMobile ? "强制覆盖" : "强制覆盖源站"}
                name="charset_force"
                valuePropName="checked"
                tooltip={!isMobile ? "源站已声明 charset 时也替换；关闭=仅在源站未声明时补齐" : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item
                label={isMobile ? "大写" : "大写编码名"}
                name="charset_uppercase"
                valuePropName="checked"
                tooltip={!isMobile ? "输出 UTF-8 而非 utf-8" : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                label={isMobile ? "类型白名单" : "应用的内容类型（可选）"}
                name="charset_content_types"
                tooltip={!isMobile ? "仅对这些 Content-Type 应用；留空=默认文本类" : undefined}
              >
                <Select
                  mode="tags"
                  allowClear
                  placeholder="text/html, application/json"
                  tokenSeparators={[',', ' ']}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>协议优化</Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={12} sm={8} md={8}>
              <Form.Item label="HTTP/2" tooltip={!isMobile ? "始终开启，浏览器自动协商" : undefined}>
                <Switch checked disabled />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? "HTTP/3" : "HTTP/3 (QUIC)"}
                name="http3_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? "启用HTTP/3协议，基于QUIC，性能更优（实验性）" : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>
            {isMobile ? "WebP 转码" : "WebP 图片转码"}
          </Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? "启用" : "启用 WebP 转码"}
                name="webp_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? "边缘按 Accept 头自动将 JPEG/PNG 转为 WebP，通常可减少 25-35% 图片体积" : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? "无损" : "无损压缩"}
                name="webp_lossless"
                valuePropName="checked"
                tooltip={!isMobile ? "启用无损 WebP（体积更大但无画质损失，适合图标/线条图）；开启时质量参数忽略" : undefined}
              >
                <Switch disabled={!webpEnabled} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? "质量" : "转码质量 (1-100)"}
                name="webp_quality"
                tooltip={!isMobile ? "有损模式下的画质，越高越清晰体积越大；默认 80" : undefined}
              >
                <InputNumber min={1} max={100} disabled={!webpEnabled} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Form.Item
                label={isMobile ? "最小字节" : "最小转码体积 (字节)"}
                name="webp_min_length"
                tooltip={!isMobile ? "图片小于此值不转码；0=无下限" : undefined}
              >
                <InputNumber min={0} step={256} disabled={!webpEnabled} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Form.Item
                label={isMobile ? "最大字节" : "最大转码体积 (字节)"}
                name="webp_max_length"
                tooltip={!isMobile ? "图片大于此值不转码（避免大图耗 CPU）；0=无上限" : undefined}
              >
                <InputNumber min={0} step={1024} disabled={!webpEnabled} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>
            {isMobile ? "Early Hints" : "Early Hints (HTTP 103)"}
          </Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col span={24}>
              <Form.Item
                label="启用 Early Hints"
                name="early_hints_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? "启用HTTP 103 Early Hints，可提升20-30%的首屏加载速度" : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {earlyHintsEnabled && (
            <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
              <Col span={24}>
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={handleAddLink}
                    size={isMobile ? 'small' : 'middle'}
                  >
                    {isMobile ? '添加资源' : '添加预加载资源'}
                  </Button>
                  <Alert
                    message={isMobile ? "预加载配置" : "配置预加载资源"}
                    description={isMobile ? "添加CSS、JS、字体等关键资源，建议3-5个" : "添加需要在Early Hints中预加载的关键资源（CSS、JS、字体等）。建议只添加3-5个最关键的资源。"}
                    type="info"
                    showIcon
                    style={{ marginTop: 12 }}
                  />
                </div>

                {isMobile ? (
                  <div>
                    {earlyHintsLinks.map((link, index) => renderMobileEarlyHintsCard(link, index))}
                    {earlyHintsLinks.length === 0 && (
                      <div style={{ textAlign: 'center', padding: 16, color: '#999' }}>
                        暂无预加载资源
                      </div>
                    )}
                  </div>
                ) : (
                  <Table
                    dataSource={earlyHintsLinks}
                    columns={[
                      {
                        title: 'URL',
                        dataIndex: 'url',
                        key: 'url',
                        width: '35%'
                      },
                      {
                        title: '资源类型',
                        dataIndex: 'as',
                        key: 'as',
                        width: '15%',
                        render: (as) => {
                          const typeMap = {
                            style: 'CSS',
                            script: 'JavaScript',
                            font: '字体',
                            image: '图片',
                            fetch: '数据',
                            document: 'HTML'
                          };
                          return typeMap[as] || as;
                        }
                      },
                      {
                        title: 'MIME类型',
                        dataIndex: 'type',
                        key: 'type',
                        width: '20%'
                      },
                      {
                        title: 'CORS',
                        dataIndex: 'crossorigin',
                        key: 'crossorigin',
                        width: '15%'
                      },
                      {
                        title: '操作',
                        key: 'action',
                        width: '15%',
                        render: (_, record, index) => (
                          <Space>
                            <Button
                              type="link"
                              icon={<EditOutlined />}
                              onClick={() => handleEditLink(record, index)}
                            >
                              编辑
                            </Button>
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteLink(index)}
                            >
                              删除
                            </Button>
                          </Space>
                        )
                      }
                    ]}
                    pagination={false}
                    size="small"
                    rowKey={(_, index) => index}
                  />
                )}
              </Col>
            </Row>
          )}

          <Alert
            message="提示"
            description={isMobile ? "更改可能需要几分钟生效" : "修改优化设置后，部分更改可能需要几分钟才能在全球CDN节点生效。建议在低峰期进行测试。"}
            type="warning"
            showIcon
            style={{ marginTop: isMobile ? 16 : 24 }}
          />
        </Form>
      </Card>

      {/* Early Hints 链接编辑Modal */}
      <Modal
        title={editingLink !== null ? '编辑预加载资源' : '添加预加载资源'}
        open={linkModalVisible}
        onOk={handleSaveLink}
        onCancel={() => {
          setLinkModalVisible(false);
          linkForm.resetFields();
        }}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { top: 20 } : undefined}
      >
        <Form
          form={linkForm}
          layout="vertical"
        >
          <Form.Item
            label="资源URL"
            name="url"
            rules={[
              { required: true, message: '请输入资源URL' },
              { pattern: /^\//, message: 'URL必须以 / 开头' }
            ]}
            tooltip="资源的相对路径，例如: /static/style.css"
          >
            <Input placeholder="/static/style.css" />
          </Form.Item>

          <Form.Item
            label="资源类型 (as)"
            name="as"
            rules={[{ required: true, message: '请选择资源类型' }]}
            tooltip="指定资源的类型，浏览器会根据此类型优化加载策略"
          >
            <Select placeholder="选择资源类型">
              <Option value="style">CSS 样式表</Option>
              <Option value="script">JavaScript 脚本</Option>
              <Option value="font">字体文件</Option>
              <Option value="image">图片</Option>
              <Option value="fetch">数据请求 (XHR/Fetch)</Option>
              <Option value="document">HTML 文档</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="MIME类型 (type)"
            name="type"
            tooltip="可选，指定资源的MIME类型，例如: font/woff2, image/webp"
          >
            <Input placeholder="font/woff2" />
          </Form.Item>

          <Form.Item
            label="跨域设置 (crossorigin)"
            name="crossorigin"
            tooltip="对于需要CORS的资源（如字体），请设置此选项"
          >
            <Select placeholder="选择跨域设置" allowClear>
              <Option value="anonymous">anonymous (不发送凭证)</Option>
              <Option value="use-credentials">use-credentials (发送凭证)</Option>
            </Select>
          </Form.Item>

          <Alert
            message="配置示例"
            description={
              <div>
                <p><strong>CSS:</strong> /static/main.css, as=style</p>
                <p><strong>字体:</strong> /fonts/main.woff2, as=font, type=font/woff2, crossorigin=anonymous</p>
                <p><strong>JS:</strong> /static/app.js, as=script</p>
              </div>
            }
            type="info"
            showIcon
          />
        </Form>
      </Modal>
    </div>
  );
}
